import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, ArrowUpRight } from 'lucide-react';
import { T } from '@/constants/testIds';
import HowNiaWorks from '@/components/HowNiaWorks';
import { NiaLogo } from '@/components/NiaLogo';

/**
 * Phase V3.9 — Sender-only landing.
 *
 * Recipients and Local Agents both moved off the Nia app onto WhatsApp:
 *   * Recipients chat with the Nia Twilio Sandbox number (menu UX).
 *   * Agents use WhatsApp commands: TICKETS / CLAIM <REF> / DONE <REF>,
 *     and upload M-Pesa / MoMo receipts via a single-use magic-link page
 *     served from /api/r/{token}.
 *
 * Result: the customer-facing app has exactly one persona — Sender.
 * Admin tools live entirely under /admin/* and aren't surfaced here.
 *
 * Phase 9 — Premium diaspora-warm redesign:
 *   * Hero is a paper-surface card with clay + gold soft radial blobs.
 *   * Wordmark in Playfair Display ink-on-cream (not white-on-emerald).
 *   * "Log in" CTA is a clay-tinted card with arrow-up-right affordance.
 *   * Disclaimer card retains the Apple-required "Not a bank, lender,
 *     or money transfer service" copy.
 */
export default function Onboarding() {
    const nav = useNavigate();
    return (
        <div className="nia-page">
            <div className="mobile-shell" style={{ overflow: 'hidden' }}>
                {/* Hero — paper surface with warm radial blobs (no dark green block). */}
                <section className="nia-hero" style={{ padding: '28px 22px 36px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <NiaLogo size={36} variant="dark" />
                        <span
                            className="nia-pill"
                            style={{
                                background: 'var(--nia-gold-tint)',
                                color: '#6B5800',
                                border: '1px solid var(--nia-gold-soft)',
                                padding: '5px 12px',
                                fontSize: 11,
                                letterSpacing: '0.06em',
                            }}
                            data-testid="landing-private-beta-pill"
                        >
                            <Lock size={11} /> Private beta
                        </span>
                    </div>

                    <h1
                        className="font-display"
                        style={{
                            fontSize: 44,
                            lineHeight: 1.04,
                            fontWeight: 700,
                            margin: '40px 0 14px',
                            letterSpacing: '-0.025em',
                            color: 'var(--nia-ink)',
                        }}
                        data-testid="landing-tagline"
                    >
                        Support Home<br />Without Stress.
                    </h1>
                    <p
                        style={{
                            color: 'var(--nia-text-muted)',
                            fontSize: 16,
                            lineHeight: 1.55,
                            fontWeight: 500,
                            margin: 0,
                            maxWidth: 320,
                        }}
                    >
                        Because Home Can&apos;t Wait.
                    </p>
                </section>

                <section style={{ padding: '24px 18px 36px' }}>
                    <div
                        className="font-display"
                        style={{
                            fontSize: 13,
                            fontWeight: 600,
                            margin: '0 0 12px',
                            letterSpacing: '0.08em',
                            color: 'var(--nia-text-subtle)',
                            textTransform: 'uppercase',
                        }}
                    >
                        Get started
                    </div>

                    <button
                        data-testid={T.onboardSelectSender}
                        onClick={() => nav('/login/sender')}
                        className="nia-card tap-row"
                        style={{
                            width: '100%',
                            padding: 18,
                            textAlign: 'left',
                            border: '1px solid var(--nia-border)',
                            display: 'flex',
                            gap: 14,
                            alignItems: 'center',
                            marginBottom: 20,
                            background: 'var(--nia-paper)',
                        }}
                    >
                        <div
                            style={{
                                width: 52,
                                height: 52,
                                borderRadius: 16,
                                background: 'linear-gradient(135deg, var(--nia-emerald) 0%, var(--nia-emerald-deep) 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                boxShadow: '0 8px 18px rgba(14, 90, 79, 0.30)',
                            }}
                        >
                            <SendCircleIcon />
                        </div>
                        <div style={{ flex: 1 }}>
                            <div
                                className="font-display"
                                style={{
                                    fontWeight: 700,
                                    fontSize: 18,
                                    color: 'var(--nia-ink)',
                                    letterSpacing: '-0.015em',
                                }}
                            >
                                Log in
                            </div>
                            <div
                                style={{
                                    color: 'var(--nia-text-muted)',
                                    fontSize: 13,
                                    marginTop: 4,
                                    lineHeight: 1.5,
                                }}
                            >
                                Plan support, set reminders, coordinate with loved ones at home.
                            </div>
                        </div>
                        <ArrowUpRight size={20} color="var(--nia-clay)" />
                    </button>

                    <HowNiaWorks variant="card" />

                    {/* Apple-safe disclaimer — must stay visible. */}
                    <div
                        className="nia-not-bank-default"
                        style={{ marginTop: 20 }}
                        data-testid="landing-disclaimer-card"
                    >
                        <ShieldDot />
                        <div>
                            <div className="nia-not-bank-title-sm">A planning tool</div>
                            <div className="nia-not-bank-body-sm">
                                Nia helps diaspora families coordinate support. Nia is not a bank, lender, or money transfer service.
                            </div>
                        </div>
                    </div>

                    <div
                        style={{
                            marginTop: 16,
                            padding: '12px 14px',
                            background: 'transparent',
                            textAlign: 'center',
                        }}
                    >
                        <div style={{ fontSize: 11, color: 'var(--nia-text-subtle)', lineHeight: 1.6 }}>
                            By continuing you agree to our{' '}
                            <a
                                href="/legal/terms"
                                style={{ color: 'var(--nia-clay)', fontWeight: 600, textDecoration: 'none' }}
                            >
                                Terms
                            </a>{' '}
                            and{' '}
                            <a
                                href="/legal/privacy"
                                style={{ color: 'var(--nia-clay)', fontWeight: 600, textDecoration: 'none' }}
                            >
                                Privacy Policy
                            </a>
                            .
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
}

function SendCircleIcon() {
    return (
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path
                d="M3 11.5L20 4L13 21L11 13L3 11.5Z"
                stroke="white"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

function ShieldDot() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
            <path
                d="M12 2.5L4 5.5V12C4 16.4 7.4 20.2 12 21.5C16.6 20.2 20 16.4 20 12V5.5L12 2.5Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
            />
            <path
                d="M8.5 12L11 14.5L15.5 10"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}
